import { useCallback, useEffect, useState } from 'react';
import type { FC, SyntheticEvent } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  FormHelperText,
  Unstable_Grid2 as Grid,
  TextField,
  Typography,
  useTheme,
  Stack,
  Alert,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z as zod } from 'zod';

import type { Quote } from '@/types/v2/quote';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import ProfessionalDialog, { DialogActionButtons } from '@/components/common/professional-dialog';

const schema = zod.object({
  community_id: zod.string(),
  request_id: zod.string(),
  total: zod.string(),
  description: zod.string().min(1, 'Description is required'),
});

type FormValues = zod.infer<typeof schema>;

interface CreateUpdateQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  requestId: string;
  communityId: string;
  quote: Quote | null;
}

const CreateUpdateQuoteDialog: FC<CreateUpdateQuoteDialogProps> = (props) => {
  const { open, onClose, onRefresh, requestId, communityId, quote } = props;
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const { user } = useUser();

  const defaultValues = {
    community_id: user?.selected_community_id ?? communityId,
    request_id: requestId,
    total: '0.00',
    description: '',
  };

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const createRequestMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const response = await axios.post(`/api/v2/communities/${communityId}/requests/${requestId}/quotes`, data);
      return response.data;
    },
  });

  const onSubmit = useCallback(
    async (data: FormValues) => {
      try {
        const response = await createRequestMutation.mutateAsync(data);
        if (response?.error) {
          setError(response.error);
          return;
        }
        toast.success(response.message, {
          description: 'Your quote has been created successfully.',
        });
        queryClient.invalidateQueries({ queryKey: ['request-quotes', requestId] });
        handleClose();
      } catch (error) {
        console.error('Error creating quote:', error);
        setError('Error creating quote');
      }
    },
    [createRequestMutation, queryClient, requestId]
  );

  const handleClose = useCallback((): void => {
    onClose();
    reset();
  }, [onClose, reset]);

  useEffect(() => {
    reset();
    if (quote) {
      setValue('description', quote.description);
      setValue('total', quote.total);
    }
  }, [quote?.id, reset, setValue]);

  return (
    <ProfessionalDialog
      actions={
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button
              onClick={handleClose}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                borderWidth: '2px',
                fontWeight: 600,
                '&:hover': {
                  borderWidth: '2px',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              disabled={isSubmitting || createRequestMutation.isPending}
              loading={isSubmitting || createRequestMutation.isPending}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                boxShadow: (theme) => `0 8px 24px ${theme.palette.primary.main}30`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: (theme) => `0 12px 32px ${theme.palette.primary.main}40`,
                },
              }}
              type="submit"
              variant="contained"
            >
              {quote ? 'Update Quote' : 'Add Quote'}
            </LoadingButton>
          </Box>
        </form>
      }
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={open}
      subtitle="Provide the details for your quote"
      title={quote ? 'Update Quote' : 'Create Quote'}
      variant="elevated"
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack mt={4} spacing={4}>
          {/* Quote Details Section */}
          <Box>
            <Typography gutterBottom sx={{ fontWeight: 600, mb: 2 }} variant="subtitle2">
              Quote Details
            </Typography>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      error={Boolean(errors.description)}
                      fullWidth
                      helperText={errors.description?.message}
                      label="Description"
                      multiline
                      placeholder="Provide a detailed description of the quote..."
                      required
                      rows={4}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid md={6} xs={12}>
                <Controller
                  control={control}
                  name="total"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      error={Boolean(errors.total)}
                      fullWidth
                      helperText={errors.total?.message}
                      inputProps={{ step: 0.01, min: 0 }}
                      label="Total Amount"
                      placeholder="0.00"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                      type="number"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Error Display */}
          {error ? <Alert severity="error" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">{error}</Typography>
            </Alert> : null}

          {/* Help Text */}
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Quote Guidelines:</strong> Provide a clear description of the work or service being quoted, 
              along with the total cost. Be specific about what you're offering and any terms or conditions.
            </Typography>
          </Alert>
        </Stack>
      </form>
    </ProfessionalDialog>
  );
};

export default CreateUpdateQuoteDialog;
